import type { MathNode } from 'mathjs';

import { math } from './math-config.ts';

/**
 * Reserved prefix for synthetic derives generated by inline-expression
 * extraction. The Arquero executor strips columns whose names start with
 * this prefix from the final output dataset so they stay invisible to the
 * caller.
 *
 * Double-underscore-prefixed names follow the Python `__dunder` convention
 * for "system-reserved" identifiers — anyone explicitly naming a column
 * with this prefix is opting into collision behaviour.
 */
export const SYNTHETIC_DERIVE_PREFIX = '__expr_';

/**
 * True if `name` is a synthetic-derive column produced by the extraction
 * pass. Used by the executor to strip these from the final output.
 */
export function isSyntheticDeriveName(name: string): boolean {
  return name.startsWith(SYNTHETIC_DERIVE_PREFIX);
}

/**
 * AST node types that mark a string as a "real" math.js expression worth
 * synthesising into a derive. Bare names (`SymbolNode`), dotted paths
 * (`AccessorNode` / `IndexNode`), constants, and parenthesised wrappers do
 * NOT trigger synthesis on their own — those pass through as literal column
 * references so the downstream operator can resolve them against
 * input columns, materialised dotted paths, OR operator-created columns
 * (pivot keys, groupBy aggregation outputs, fold key/value names, reshape
 * outputs) that don't exist yet at extraction time.
 */
const EXPRESSION_NODE_TYPES = new Set([
  'OperatorNode',
  'FunctionNode',
  'ConditionalNode',
  'ArrayNode',
  'ObjectNode',
  'RangeNode',
  'BlockNode',
  'AssignmentNode',
  'FunctionAssignmentNode',
]);

/**
 * True when `text` parses as a math.js expression that contains at least one
 * operator/function/conditional/etc. node. Used as the second classification
 * gate after the literal-column match — see {@link extractInlineExpressions}.
 *
 * Anything that fails to parse, or parses to a pure name/access/constant
 * tree, returns false and is left as-is by the extraction pass.
 */
export function isExpressionAst(text: string): boolean {
  // `math.parse` is overloaded on input — array input returns `MathNode[]`,
  // single string returns `MathNode`. We always pass a single string, so the
  // runtime branch is always `MathNode` — narrow at the boundary.
  let root: MathNode;
  try {
    root = math.parse(text) as MathNode;
  } catch {
    return false;
  }
  // `traverse` visits the root and all descendants. Using a closure-captured
  // boolean keeps the walk linear without an early-exit mechanism in math.js
  // — for the short expression strings we hash, the cost is negligible.
  let hasExprNode = false;
  root.traverse((node: MathNode) => {
    if (EXPRESSION_NODE_TYPES.has(node.type)) {
      hasExprNode = true;
    }
  });
  return hasExprNode;
}

/**
 * FNV-1a 32-bit hash, base-36 encoded. Fast, deterministic, no crypto dep
 * required for non-security hashing of LLM-generated short strings — the
 * goal is "identical expression text → identical synthetic derive name",
 * which a 32-bit hash satisfies for the realistic spec size.
 */
export function stableHash(input: string): string {
  const FnvOffset = 0x811c9dc5;
  const FnvPrime = 0x01000193;
  const bytes = Array.from(input, (ch) => ch.charCodeAt(0));
  const h = bytes.reduce((acc, byte) => Math.imul(acc ^ byte, FnvPrime), FnvOffset);
  return (h >>> 0).toString(36);
}
